// src/components/fix-actions/redesign/shared.tsx
//
// Shared design foundation for the Action Items redesign.
// Everything routes through CSS tokens (never hardcoded hex) and maps to the
// REAL data model (ArtifactType / ArtifactStatus), not the DC mock names.

import React from "react";

/* ────────────────────────────────────────────────────────────────────────
 * Asset type → accent color (locked mapping, real ArtifactType union)
 * ──────────────────────────────────────────────────────────────────────── */
const TYPE_ACCENT: Record<string, string> = {
  "Comparison Asset": "var(--signal-blue)",
  "Voice & Messaging": "var(--spectral-violet)",
  "Evidence & Citation Pack": "var(--alpha-teal)",
  "Decision Framework": "var(--spectral-violet)",
  "Structured Data / Schema": "var(--muted-foreground)",
  "Data Artifact": "var(--signal-blue)",
  "FAQ / Knowledge Base": "var(--alpha-teal)",
  "Scenario-Based Q&A": "var(--signal-green)",
  "Boundary / Trust Artifact": "var(--infrared-coral)",
};

export function typeColor(type: string): string {
  return TYPE_ACCENT[type] || "var(--muted-foreground)";
}

/* ────────────────────────────────────────────────────────────────────────
 * "Explain this" popover copy, keyed by real ArtifactType.
 * blurb + 3 bullets + pro-tip, brand voice. Draft for Swen review.
 * ──────────────────────────────────────────────────────────────────────── */
export interface AssetTypeInfo {
  blurb: string;
  bullets: string[];
  tip: string;
}

export const ASSET_TYPE_INFO: Record<string, AssetTypeInfo> = {
  "Comparison Asset": {
    blurb:
      "When someone asks an AI to compare options in your category, the model leans on explicit comparison structures. A clear, verified comparison makes yours the framing it repeats.",
    bullets: [
      "Feature and criteria tables",
      "When-to-choose guidance",
      "Head-to-head against named alternatives",
    ],
    tip: "Models weight comparison content heavily on “X vs Y” and “alternatives to X” prompts.",
  },
  "Voice & Messaging": {
    blurb:
      "On-page copy is the primary citation source. Leading with verified, repeatable claims makes them the exact phrasing models reach for when they describe you.",
    bullets: [
      "Above-the-fold claims",
      "Consistent, verified wording",
      "Owned-site authority",
    ],
    tip: "Repeated, consistent claims across pages become the default association models cite.",
  },
  "Evidence & Citation Pack": {
    blurb:
      "Models hedge when proof is buried in prose. A structured evidence pack gives them extractable, attributable facts to cite instead of guessing.",
    bullets: [
      "Structured proof points",
      "Sourced, attributable claims",
      "Crawlable outcome data",
    ],
    tip: "Concrete, cited evidence reduces “it depends” qualifiers in answers about you.",
  },
  "Decision Framework": {
    blurb:
      "When users ask how to choose, models reproduce whatever decision framework they can find. Supply a neutral, citable one and you shape the evaluation criteria.",
    bullets: [
      "Evaluation criteria",
      "Use-case scenarios",
      "Neutral, citation-ready framing",
    ],
    tip: "Frameworks influence which criteria models treat as standard for the category.",
  },
  "Structured Data / Schema": {
    blurb:
      "Machine-readable structured data describes your entities, offers, and claims so crawlers and LLMs extract facts without inference.",
    bullets: [
      "Organization + Offer schema",
      "Claim markup",
      "Crawlable, unambiguous facts",
    ],
    tip: "Structured data is the highest-confidence signal for factual extraction.",
  },
  "Data Artifact": {
    blurb:
      "A structured data artifact turns your numbers into something models can quote precisely, instead of paraphrasing or rounding away the detail.",
    bullets: [
      "Structured metrics and figures",
      "Machine-readable values",
      "Consistent across surfaces",
    ],
    tip: "Precise, structured figures are cited far more reliably than the same numbers in prose.",
  },
  "FAQ / Knowledge Base": {
    blurb:
      "Structured question-and-answer content gives models clean, citable answers to the exact questions users ask, which cuts hedging.",
    bullets: [
      "Direct Q&A pairs",
      "FAQPage structured data",
      "Plain-language answers",
    ],
    tip: "Models surface FAQ answers close to verbatim when the question matches a user prompt.",
  },
  "Scenario-Based Q&A": {
    blurb:
      "Scenario-based Q&A anticipates the situational questions users actually ask, so models can map a real context to a verified answer about you.",
    bullets: [
      "Situational prompts",
      "Context-to-answer mapping",
      "Verified, specific responses",
    ],
    tip: "Scenario coverage helps models answer “for my situation” prompts with your framing.",
  },
  "Boundary / Trust Artifact": {
    blurb:
      "Trust and boundary content states clearly what is and is not true, so models stop inferring claims you never made and stop repeating ones you want corrected.",
    bullets: [
      "Explicit scope and limits",
      "Corrections to common misreads",
      "Trust and safety framing",
    ],
    tip: "Clear boundaries reduce fabricated or outdated claims models attach to your brand.",
  },
};

/* ────────────────────────────────────────────────────────────────────────
 * Status: backend value → user-facing pipeline label.
 * Pipeline (locked): Draft -> In Review -> Ready to publish -> Published.
 *  - backend "Ready" and "Ready to Launch" collapse to "Ready to publish"
 *  - backend "Deployed" surfaces as "Published" (never show "Deployed")
 *  - DC-only "Verified" collapses into "Published"
 * ──────────────────────────────────────────────────────────────────────── */
export type PipelineLabel = "Draft" | "In Review" | "Ready to publish" | "Published";

export const PIPELINE_ORDER: PipelineLabel[] = [
  "Draft",
  "In Review",
  "Ready to publish",
  "Published",
];

export function statusLabel(raw: string | undefined | null): PipelineLabel {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "draft" || s === "") return "Draft";
  if (s === "in review" || s === "in_review" || s === "in-review") return "In Review";
  if (s === "ready" || s === "ready to launch" || s === "ready to publish")
    return "Ready to publish";
  if (s === "deployed" || s === "published" || s === "verified") return "Published";
  return "Draft";
}

export interface StatusVisual {
  bg: string;
  text: string;
  border: string;
  bar: string;
}

export function statusVisual(label: PipelineLabel): StatusVisual {
  switch (label) {
    case "In Review":
      return {
        bg: "var(--warning-bg)",
        text: "var(--warning-text)",
        border: "var(--warning-border)",
        bar: "var(--infrared-coral)",
      };
    case "Ready to publish":
      return {
        bg: "var(--success-bg)",
        text: "var(--success-text)",
        border: "var(--success-border)",
        bar: "var(--signal-green)",
      };
    case "Published":
      return {
        bg: "var(--signal-blue-10)",
        text: "var(--signal-blue)",
        border: "var(--signal-blue-20)",
        bar: "var(--signal-blue)",
      };
    case "Draft":
    default:
      return {
        bg: "var(--muted)",
        text: "var(--muted-foreground)",
        border: "var(--border)",
        bar: "var(--silver-mist)",
      };
  }
}

export function statusPillCss(label: PipelineLabel): React.CSSProperties {
  const v = statusVisual(label);
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 9px",
    borderRadius: 9999,
    background: v.bg,
    color: v.text,
    border: `1px solid ${v.border}`,
    whiteSpace: "nowrap",
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * Source module badge. Real FixAction.sourceModule values: antenna |
 * cognition | suggested. Labels match current module names.
 * ──────────────────────────────────────────────────────────────────────── */
export interface SourceBadge {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string[];
}

export function sourceBadge(src: string | undefined): SourceBadge {
  const s = String(src || "").toLowerCase();
  if (s === "cognition")
    return {
      label: "AI Reasoning Path",
      color: "var(--spectral-violet)",
      bg: "var(--spectral-violet-10)",
      border: "var(--spectral-violet-20)",
      icon: ["M6 3v12", "M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z", "M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z", "M15 6a9 9 0 0 0-9 9"],
    };
  // antenna + suggested + fallback -> Brand Monitoring
  return {
    label: "Brand Monitoring",
    color: "var(--teal-text, var(--alpha-teal))",
    bg: "var(--alpha-teal-10)",
    border: "var(--alpha-teal-20)",
    icon: ["M4.9 19.1C1 15.2 1 8.8 4.9 4.9", "M7.8 16.2a6 6 0 0 1 0-8.4", "M16.2 7.8a6 6 0 0 1 0 8.4", "M19.1 4.9C23 8.8 23 15.1 19.1 19"],
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * Small inline-SVG icon helper (stroke = currentColor).
 * ──────────────────────────────────────────────────────────────────────── */
export function Ic({
  paths,
  size = 14,
  sw = 1.7,
  color,
}: {
  paths: string[];
  size?: number;
  sw?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color || "currentColor"}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

export const SPARKLE = [
  "m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z",
];

/* ────────────────────────────────────────────────────────────────────────
 * Confidence ring (circular progress).
 * ──────────────────────────────────────────────────────────────────────── */
export function ConfRing({
  pct,
  size = 40,
  ready = true,
}: {
  pct: number;
  size?: number;
  ready?: boolean;
}) {
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const col = pct >= 80 ? "var(--signal-green)" : "var(--infrared-coral)";
  const off = ready ? c * (1 - pct / 100) : c;
  return (
    <div title={`AI confidence: ${pct}%`} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--muted)" strokeWidth={2.5} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={col}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset .9s var(--ease-standard)" }}
        />
      </svg>
      <span
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size >= 44 ? 12 : 11,
          fontWeight: 700,
          color: col,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {pct}
      </span>
    </div>
  );
}
